/** Reporting engine — application shell (BrightGauge replacement).
 *
 * Toolbar (client + month selector + live-sync chip + Edit layout + Add widget +
 * Download report), the draggable widget grid, and the drill-down + builder
 * overlays. The widget layout is a single GLOBAL template served by the backend:
 * every report viewer loads the same layout, and only iQuest staff may edit it
 * (the authoring controls are hidden for clients and the server enforces it).
 * Staff edits auto-save (debounced) to the server, so one edit reaches every
 * customer. iQuest staff pick a client (the server requires ?company_id for the
 * ticket feed); client users are pinned to their own company server-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import useSWR from "swr";

import { api, type Me } from "../services/api";

import "./reporting.css";
import { Board, defaultWidgets } from "./Board";
import { C, MONO, SANS } from "./data";
import { FocusExplorer } from "./FocusExplorer";
import type { Drill, Widget } from "./types";
import { WidgetBuilder } from "./WidgetBuilder";

const STAFF_ROLES = ["iquest_admin", "service_desk"];

// Debounce between a layout edit and the PUT that persists it, so a drag/resize
// burst coalesces into a single save.
const SAVE_DEBOUNCE_MS = 800;

// Legacy per-user localStorage keys from the old client-side layout. We no longer
// read them (the server template is authoritative); clear any stragglers so a
// stale local layout can never shadow the global one.
const LEGACY_KEY_PREFIX = "iquest_reporting_widgets::";

function daysInMonth(period: string): number {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
}

function clearLegacyLayoutKeys(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LEGACY_KEY_PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    /* storage disabled/unavailable: nothing to clean */
  }
}

export function ReportingDashboard() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const isStaff = !!me && STAFF_ROLES.includes(me.role);

  // Staff pick a client; client users are pinned server-side (companyId omitted).
  // The list comes from the reporting router's own staff-scoped picker, not the
  // iquest_admin-only admin clients list: service_desk reads these reports too,
  // and that list 403'd them into an empty dropdown (#157).
  const { data: clients, error: clientsError } = useSWR(
    isStaff ? "reporting:clients" : null,
    () => api.reporting.companies(),
  );
  const [companyId, setCompanyId] = useState<string>("");
  const effectiveCompanyId = isStaff ? companyId || undefined : undefined;
  const canQuery = !!me && me.may_view_reports !== false && (!isStaff || !!companyId);

  const { data: periodsResp } = useSWR(
    canQuery ? ["reporting:periods", effectiveCompanyId] : null,
    () => api.reporting.periods(effectiveCompanyId),
  );
  const periods = useMemo(() => periodsResp?.periods ?? [], [periodsResp]);

  const [periodKey, setPeriodKey] = useState<string | null>(null);

  // Deep link from the monthly review email: /reports?period=YYYY-MM opens that
  // month instead of the live one, so "View your July 2026 report" lands on the
  // finished July board. Read once on mount (the selector, not the URL, owns the
  // month from then on) and only honoured if the month is still selectable, so a
  // link whose month has aged out of retention falls back to the default rather
  // than rendering an empty board.
  const requestedPeriod = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get("period");
    return raw && /^\d{4}-\d{2}$/.test(raw) ? raw : null;
  }, []);
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current || !requestedPeriod || periods.length === 0) return;
    deepLinkedRef.current = true;
    if (periods.some((p) => p.period === requestedPeriod)) setPeriodKey(requestedPeriod);
  }, [periods, requestedPeriod]);

  const activePeriod =
    periodKey ??
    periods.find((p) => p.is_live)?.period ??
    periods[periods.length - 1]?.period ??
    null;
  const activeMeta = periods.find((p) => p.period === activePeriod) ?? null;
  const isLive = !!activeMeta?.is_live;

  const { data: ticketsResp, isValidating } = useSWR(
    canQuery && activePeriod ? ["reporting:tickets", effectiveCompanyId, activePeriod] : null,
    () => api.reporting.tickets(activePeriod as string, effectiveCompanyId),
    { refreshInterval: isLive ? 300000 : 0 },
  );
  const tickets = ticketsResp?.tickets ?? [];
  const days = activePeriod ? daysInMonth(activePeriod) : 31;

  // The widget layout is a single GLOBAL template from the server (replaces the
  // old per-user localStorage layout). Any report viewer may read it; only iQuest
  // staff may edit, and staff edits auto-save (debounced) so one edit reaches
  // every customer.
  const canView = !!me && me.may_view_reports !== false;
  const { data: template, mutate: mutateTemplate } = useSWR(
    canView ? "reporting:dashboard" : null,
    () => api.reporting.dashboard(),
  );

  const [widgets, setWidgets] = useState<Widget[]>(defaultWidgets());
  const versionRef = useRef<number>(1);
  // JSON of the layout the server is known to hold. A save fires only when the
  // live widgets differ from this, so hydration and no-op re-renders never save,
  // and seeding the built-in defaults for an empty template doesn't auto-persist.
  const syncedRef = useRef<string>("");
  // Gate saves until the server template has hydrated once, so a staff member's
  // initial default layout is never auto-PUT before we know the server's state.
  const hydratedRef = useRef(false);
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error" | "conflict"
  >("idle");

  // One-time cleanup of legacy per-user localStorage layouts.
  useEffect(() => {
    clearLegacyLayoutKeys();
  }, []);

  // Hydrate from the server template. An empty template shows the built-in
  // defaults (a staff member's first save persists them); syncedRef is set to
  // whatever we display so merely loading never triggers a save.
  useEffect(() => {
    if (!template) return;
    versionRef.current = template.version;
    const incoming = template.widgets as Widget[];
    const next = incoming.length ? incoming : defaultWidgets();
    syncedRef.current = JSON.stringify(next);
    hydratedRef.current = true;
    setWidgets(next);
    setSaveState("idle");
  }, [template]);

  const saveTemplate = useCallback(
    async (next: Widget[], snapshot: string) => {
      setSaveState("saving");
      try {
        const res = await api.reporting.saveDashboard(next, versionRef.current);
        versionRef.current = res.version;
        syncedRef.current = snapshot;
        setSaveState("saved");
      } catch (e) {
        const msg = (e as Error).message ?? "";
        if (msg.startsWith("409")) {
          // Someone else saved meanwhile: reload the authoritative layout rather
          // than clobber it. The staff editor's unsaved changes are dropped.
          setSaveState("conflict");
          const fresh = await mutateTemplate();
          if (fresh) {
            versionRef.current = fresh.version;
            const w = (fresh.widgets as Widget[]).length
              ? (fresh.widgets as Widget[])
              : defaultWidgets();
            syncedRef.current = JSON.stringify(w);
            setWidgets(w);
          }
        } else {
          setSaveState("error");
        }
      }
    },
    [mutateTemplate],
  );

  // Debounced auto-save: staff only, and only when the layout actually changed
  // versus what the server holds. A drag/resize burst coalesces into one PUT.
  useEffect(() => {
    if (!isStaff || !hydratedRef.current) return;
    const snapshot = JSON.stringify(widgets);
    if (snapshot === syncedRef.current) return;
    const t = setTimeout(() => void saveTemplate(widgets, snapshot), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [widgets, isStaff, saveTemplate]);

  // Board calls this after a mutation; saving is driven by the debounced effect
  // above, so it is a no-op kept only for the Board contract.
  const persist = useCallback(() => {}, []);

  const [editMode, setEditMode] = useState(false);
  const [building, setBuilding] = useState(false);
  // The widget open in the builder for editing; null when building a new one.
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);
  // The open drill-down: the widget, plus the breakdown row it was opened from
  // when it was opened from one rather than from the card itself (issue #117).
  const [focus, setFocus] = useState<{ widget: Widget; drill?: Drill } | null>(null);
  const openFocus = useCallback(
    (widget: Widget, drill?: Drill) => setFocus({ widget, drill }),
    [],
  );
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState<string | null>(null);

  const addWidget = (w: Widget) => {
    const maxY = Math.max(0, ...widgets.map((x) => x.pos.y + x.pos.h));
    setWidgets((ws) => [...ws, { ...w, pos: { ...w.pos, x: 0, y: maxY } }]);
    setBuilding(false);
    setEditMode(true);
  };

  const saveWidget = (next: Widget) => {
    setWidgets((ws) => ws.map((w) => (w.id === next.id ? next : w)));
    setEditingWidget(null);
  };

  async function downloadPdf() {
    if (!activePeriod) return;
    setDownloading(true);
    setDownloadErr(null);
    try {
      const { blob, filename } = await api.reporting.downloadReport(activePeriod, effectiveCompanyId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadErr((e as Error).message ?? "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  // --- access / empty gates (all hooks above run unconditionally) ---
  if (me && me.may_view_reports === false) {
    return (
      <div className="rp-root">
        <Notice title="Reporting is not available for your account">
          Ask your iQuest contact if you need access to the reporting dashboard.
        </Notice>
      </div>
    );
  }

  return (
    <div className="rp-root">
      <header style={styles.toolbar}>
        {/* No company name under the title: a customer knows who they are, and
            iQuest staff read the client off the selector beside it. The service
            review PDF still carries it — a document leaves the portal and has to
            say who it is about. */}
        <div style={styles.title}>Service Dashboard</div>
        <div style={styles.tbRight}>
          {isStaff && (
            <select
              aria-label="Client"
              value={companyId}
              onChange={(e) => {
                setCompanyId(e.target.value);
                setPeriodKey(null);
                setFocus(null);
              }}
              style={styles.select}
            >
              <option value="">Select a client…</option>
              {(clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          {periods.length > 0 && (
            <select
              aria-label="Reporting month"
              value={activePeriod ?? ""}
              onChange={(e) => {
                setPeriodKey(e.target.value);
                setFocus(null);
              }}
              style={styles.select}
            >
              {periods.map((p) => (
                <option key={p.period} value={p.period}>
                  {p.label}
                  {p.is_live ? " (live)" : p.partial ? " (partial)" : ""}
                </option>
              ))}
            </select>
          )}
          {isLive && (
            <span style={styles.liveChip}>
              <i style={styles.liveDot} />
              {isValidating ? "syncing…" : "live"}
            </span>
          )}
          <span style={styles.divider} />
          {/* Authoring the shared global layout is iQuest-staff only; clients get
              a read-only dashboard (they can still drill down and download). */}
          {isStaff && (
            <>
              {saveState !== "idle" && (
                <span style={styles.saveChip} title="Layout changes are saved for all customers">
                  {saveState === "saving"
                    ? "saving…"
                    : saveState === "saved"
                      ? "✓ saved"
                      : saveState === "conflict"
                        ? "reloaded — another edit won"
                        : "save failed"}
                </span>
              )}
              <button type="button" style={editMode ? styles.btnActive : styles.btn} onClick={() => setEditMode((e) => !e)} disabled={!canQuery}>
                {editMode ? "✓ Done" : "Edit layout"}
              </button>
              <button type="button" style={styles.btn} onClick={() => setBuilding(true)} disabled={!canQuery}>
                ＋ Add widget
              </button>
            </>
          )}
          <button type="button" style={styles.btnGhost} onClick={downloadPdf} disabled={!canQuery || !activePeriod || downloading}>
            {downloading ? "Preparing…" : "Download report ↧"}
          </button>
        </div>
      </header>

      {editMode && (
        <div style={styles.editBar}>
          <span style={{ fontFamily: MONO, fontSize: 11, color: C.forest }}>
            EDIT MODE —{" "}
            <span className="rp-edit-draghint">
              drag headers to move · drag the corner to resize ·{" "}
            </span>
            ✎ edits type, metric & filters · 🔒 locks a widget
          </span>
          <button type="button" style={styles.resetLink} onClick={() => setWidgets(defaultWidgets())}>
            Reset to default layout
          </button>
        </div>
      )}

      <main style={styles.main}>
        {downloadErr && <Notice title="Could not generate the report">{downloadErr}</Notice>}
        {/* A picker that fails to load must say so: a silently empty dropdown is
            exactly how #157 hid for weeks. */}
        {isStaff && clientsError && (
          <Notice title="Could not load the client list">
            {clientsError instanceof Error ? clientsError.message : String(clientsError)}
          </Notice>
        )}
        {isStaff && !companyId ? (
          <Notice title="Choose a client to begin">
            Select a client above to view their service dashboard.
          </Notice>
        ) : !activePeriod ? (
          <Notice title="No reporting data yet">
            There are no tickets in the reporting window for this client yet.
          </Notice>
        ) : (
          <>
            {!isLive && activeMeta?.partial && (
              <div style={styles.archNotice}>
                Viewing a <b>partial period</b> — {activeMeta.label}. The earliest month in the
                window is not a full calendar month.
              </div>
            )}
            {tickets.length === 0 ? (
              <Notice title="No tickets this month">
                {activeMeta?.label} has no tickets in scope.
              </Notice>
            ) : (
              <Board
                widgets={widgets}
                setWidgets={setWidgets}
                editMode={editMode}
                tickets={tickets}
                prevTickets={null}
                days={days}
                onOpen={openFocus}
                onEdit={setEditingWidget}
                persist={persist}
              />
            )}
          </>
        )}
      </main>

      {focus && (
        <FocusExplorer
          // Keyed on the picked row so opening a different one remounts: the
          // popout's "show everything instead" state must not carry over from the
          // last drill (issue #117).
          key={`${focus.widget.id}::${focus.drill?.label ?? ""}`}
          widget={focus.widget}
          monthTickets={tickets}
          days={days}
          monthLabel={activeMeta?.label ?? ""}
          live={isLive}
          drill={focus.drill}
          onClose={() => setFocus(null)}
        />
      )}
      {building && (
        <WidgetBuilder monthTickets={tickets} days={days} onAdd={addWidget} onClose={() => setBuilding(false)} />
      )}
      {editingWidget && (
        <WidgetBuilder
          monthTickets={tickets}
          days={days}
          // Remount per widget so the drawer seeds from the one just opened.
          key={editingWidget.id}
          initial={editingWidget}
          onSave={saveWidget}
          onAdd={addWidget}
          onClose={() => setEditingWidget(null)}
        />
      )}
    </div>
  );
}

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.rule}`,
        borderLeft: `3px solid ${C.forest}`,
        padding: "14px 16px",
        borderRadius: 2,
        margin: "18px 0",
      }}
    >
      <div style={{ fontFamily: SANS, fontWeight: 600, fontSize: 14, color: C.ink, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: C.mud }}>{children}</div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: "12px 0 14px",
    borderBottom: `1px solid ${C.rule}`,
    flexWrap: "wrap",
    minWidth: 0,
    maxWidth: "100%",
  },
  title: { fontFamily: SANS, fontWeight: 600, fontSize: 16, color: C.ink, letterSpacing: "-0.01em", lineHeight: 1.1 },
  tbRight: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0, maxWidth: "100%" },
  select: { font: "inherit", fontSize: 12.5, padding: "7px 10px", border: `1px solid ${C.rule}`, borderRadius: 2, background: "#fff", color: C.ink, maxWidth: "100%", minWidth: 0 },
  liveChip: { display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 11px", background: "#f0f6ef", border: `1px solid ${C.rule}`, borderRadius: 2, fontFamily: MONO, fontSize: 11, color: C.forest },
  saveChip: { display: "inline-flex", alignItems: "center", padding: "6px 10px", background: "#f0f6ef", border: `1px solid ${C.rule}`, borderRadius: 2, fontFamily: MONO, fontSize: 11, color: C.forest, whiteSpace: "nowrap" },
  liveDot: { width: 8, height: 8, borderRadius: "50%", background: C.green, border: `1px solid ${C.forest}`, display: "inline-block", animation: "rp-pulse 1.6s infinite" },
  divider: { width: 1, height: 24, background: C.rule, margin: "0 2px" },
  btn: { font: "inherit", cursor: "pointer", padding: "8px 14px", fontSize: 12.5, fontWeight: 500, fontFamily: SANS, background: "#fff", color: C.ink, border: `1px solid ${C.rule}`, borderRadius: 2 },
  btnActive: { font: "inherit", cursor: "pointer", padding: "8px 14px", fontSize: 12.5, fontWeight: 600, fontFamily: SANS, background: C.ink, color: C.green, border: `1px solid ${C.ink}`, borderRadius: 2 },
  btnGhost: { font: "inherit", cursor: "pointer", padding: "8px 14px", fontSize: 12.5, fontWeight: 500, fontFamily: SANS, background: "transparent", color: C.forest, border: `1px solid ${C.forest}`, borderRadius: 2 },
  editBar: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, padding: "8px 0", marginTop: 8, borderBottom: `1px solid ${C.rule}`, minWidth: 0, maxWidth: "100%" },
  resetLink: { font: "inherit", cursor: "pointer", border: "none", background: "transparent", color: C.orange, fontFamily: MONO, fontSize: 11, textDecoration: "underline" },
  main: { padding: "18px 0", minHeight: 300 },
  archNotice: { background: "#fff", border: `1px solid ${C.rule}`, borderLeft: `3px solid ${C.stone}`, padding: "10px 14px", marginBottom: 18, fontSize: 12.5, color: C.mud, borderRadius: 2 },
};
